import {
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { config } from "@/lib/config";

let pca: PublicClientApplication | null = null;
let initPromise: Promise<void> | null = null;

function buildConfig(): Configuration {
  const tenantId = config.azureAd.tenantId ?? "common";
  const clientId = config.azureAd.clientId ?? "00000000-0000-0000-0000-000000000000";
  return {
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: "sessionStorage",
      storeAuthStateInCookie: false,
    },
  };
}

export function getMsal(): PublicClientApplication {
  if (!pca) pca = new PublicClientApplication(buildConfig());
  return pca;
}

export async function ensureMsalInitialized(): Promise<void> {
  if (!config.isAuthEnabled()) return;
  if (!initPromise) {
    initPromise = getMsal().initialize();
  }
  await initPromise;
}

export function getActiveAccount(): AccountInfo | null {
  if (!config.isAuthEnabled()) return null;
  return getMsal().getActiveAccount() ?? getMsal().getAllAccounts()[0] ?? null;
}

export async function login(): Promise<void> {
  await ensureMsalInitialized();
  const scopes = scopeList();
  await getMsal().loginRedirect({ scopes });
}

export async function logout(): Promise<void> {
  await ensureMsalInitialized();
  await getMsal().logoutRedirect();
}

export async function acquireApiToken(): Promise<string | null> {
  if (!config.isAuthEnabled()) return null;
  await ensureMsalInitialized();
  const account = getActiveAccount();
  if (!account) return null;
  const scopes = scopeList();
  try {
    const r = await getMsal().acquireTokenSilent({ account, scopes });
    return r.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      await getMsal().acquireTokenRedirect({ scopes });
    }
    return null;
  }
}

function scopeList(): string[] {
  const scope = config.azureAd.apiScope;
  return scope ? [scope] : ["User.Read"];
}
